import { Tooltip } from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import noImage from "assets/images/no-image.png";
import Button from "components/common/Button";
import api from "config/api";
import { useAppSelector } from "hooks/store";
import React from "react";
import { useEffect } from "react"; // === ADDED BY SAMPATH – 2026-01-14 ===
// === END SAMPATH ===
import { useState } from "react";
type Props = any;

const Product = (props: Props) => {
  const { settings, deliveryType } = useAppSelector(({ main }) => main);
  const [loading, setLoading] = useState(true);
  const [toppingPriceRows, setToppingPriceRows] = useState<any[]>([]);
  const [pizzaSizeRows, setPizzaSizeRows] = useState<any[]>([]);

  // === ADDED BY SAMPATH – 2026-01-14 ===
  // Always show a default image when product image is not selected
  const rawImageUrl =
    props?.imageUrl && String(props.imageUrl).trim()
      ? String(props.imageUrl).trim()
      : "";
  const resolvedImageUrl =
    rawImageUrl && props?.__imageCacheBuster
      ? `${rawImageUrl}${rawImageUrl.includes("?") ? "&" : "?"}v=${
          props.__imageCacheBuster
        }`
      : rawImageUrl || noImage;

  // Since we always have an image (real or default), treat as "has image" for layout
  const hasAnyImage = true;

  // Reset loading whenever image source changes (important for default image too)
  useEffect(() => {
    setLoading(true);
  }, [resolvedImageUrl]);
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-03-05 ===
  // Safe numeric parser to avoid undefined / null / string issues
  const toSafeNumber = (value: any) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  // Determine best available base price safely
  const safeBasePrice =
    toSafeNumber(props?.price) ??
    toSafeNumber(props?.takeoutprice) ??
    toSafeNumber(props?.deliveryprice);

  // Detect whether this looks like a pizza-type product with bad / missing size configuration
  const pizzaSizeSource =
    props?.pizzaSize ??
    props?.pizzaSizes ??
    props?.sizes ??
    props?.variants ??
    props?.productSizes;

  const hasPizzaSizeIssue =
    props?.isPizza === true ||
    props?.productType === "pizza" ||
    props?.categoryName === "pizza" ||
    props?.category === "pizza"
      ? !pizzaSizeSource ||
        (Array.isArray(pizzaSizeSource) && pizzaSizeSource.length === 0)
      : false;

  // Admin-side issue flag: do NOT mark missing image as issue
  const hasProductIssue =
    !props?.productName ||
    safeBasePrice === null ||
    hasPizzaSizeIssue;

  // Support multiple possible admin-view props without breaking current usage
  const showAdminIssueFlag =
    !!(
      props?.adminPanel ||
      props?.isAdmin ||
      props?.admin ||
      props?.showAdminFlags
    ) && hasProductIssue;

  // Helpful tooltip text for admin users
  const adminIssueText = [
    !props?.productName ? "Missing product name" : "",
    safeBasePrice === null ? "Missing price" : "",
    hasPizzaSizeIssue ? "Pizza size configuration missing" : "",
  ]
    .filter(Boolean)
    .join(" • ");

  const isAdminInventoryCard =
    !!(
      props?.adminPanel ||
      props?.isAdmin ||
      props?.admin ||
      props?.showAdminFlags
    ) || window.location.pathname.includes("admin");
  const isTruthyFlag = (value: any) =>
    value === true || Number(value || 0) === 1 || String(value || "").toLowerCase() === "true";
  const isPizzaProduct = isTruthyFlag(props?.isPizza ?? props?.IsPizza);
  const isDealProduct = isTruthyFlag(props?.isDeal ?? props?.IsDeal);
  const isCreateYourOwnProduct = isTruthyFlag(
    props?.isCreateYourOwn ?? props?.IsCreateYourOwn,
  );
  const isServiceSubProduct = isTruthyFlag(props?.isServiceSub ?? props?.IsServiceSub);
  const combinedToppingCount = Number(
    props?.totalCombinetoppings ??
      props?.totalCombineToppings ??
      props?.CombinedTopping ??
      props?.combinedTopping ??
      props?.TotalCombinetoppings ??
      props?.TotalCombineToppings ??
      0,
  );
  const pizzasInDealCount = Number(props?.pizzasInDeal ?? props?.PizzasInDeal ?? 0);
  const showAdminProductMeta =
    isAdminInventoryCard &&
    (isPizzaProduct ||
      isDealProduct ||
      isCreateYourOwnProduct ||
      isServiceSubProduct ||
      combinedToppingCount > 0 ||
      pizzasInDealCount > 0);
  const adminProductMetaItems = [
    { label: "IsPizza", value: isPizzaProduct ? "Yes" : "No" },
    { label: "Pizzas", value: String(pizzasInDealCount) },
    { label: "Create Own", value: isCreateYourOwnProduct ? "Yes" : "No" },
    { label: "Deal", value: isDealProduct ? "Yes" : "No" },
    { label: "Combined", value: String(combinedToppingCount) },
    { label: "IsSub", value: isServiceSubProduct ? "Yes" : "No" },
  ];
  const getToppingPriceItems = (row: any) =>
    Array.from({ length: 5 }, (_, index) => {
      const count = index + 1;
      const price = Number(row?.[`toppingPrice${count}`] || 0);
      return price > 0 ? { count, price } : null;
    }).filter(Boolean) as { count: number; price: number }[];
  const localToppingPriceRows = [
    {
      sizeNameAbbr: "",
      sizeName: "Topping",
      toppingPrice1: props?.toppingPrice1,
      toppingPrice2: props?.toppingPrice2,
      toppingPrice3: props?.toppingPrice3,
      toppingPrice4: props?.toppingPrice4,
      toppingPrice5: props?.toppingPrice5,
    },
  ].filter((row) => getToppingPriceItems(row).length > 0);
  const displayToppingPriceRows = toppingPriceRows.length
    ? toppingPriceRows
    : localToppingPriceRows;
  const showAdminToppingPrices =
    isAdminInventoryCard &&
    displayToppingPriceRows.some((row) => getToppingPriceItems(row).length > 0);
  const getSizePriceItems = (row: any) =>
    Array.from({ length: 5 }, (_, index) => {
      const count = index + 1;
      const price = Number(row?.[`sizePriceX${count}`] || 0);
      return price > 0 ? { count, price } : null;
    }).filter(Boolean) as { count: number; price: number }[];
  const getSizePriceDescText = (rows: any[] = []) =>
    rows
      .map((row: any) => {
        const sizeName = String(
          row?.sizeNameAbbr || row?.sizeName || row?.size || ""
        ).trim();
        const label = sizeName.charAt(0).toUpperCase();
        const toppingTierPrice = Object.entries(row || {})
          .map(([key, value]: any) => {
            const match = /^toppingPrice(\d+)$/i.exec(key);
            return match && Number(value || 0) > 0
              ? { count: Number(match[1]), price: Number(value) }
              : null;
          })
          .filter(Boolean)
          .sort((a: any, b: any) => a.count - b.count)[0]?.price;
        const price = Number(
          row?.sizePriceX1 || row?.price || toppingTierPrice || 0
        );

        if (!label || !Number.isFinite(price) || price <= 0) return "";
        return `${label}: $${price.toFixed(2)}`;
      })
      .filter(Boolean)
      .join(" ");
  const showAdminSizePrices =
    isAdminInventoryCard &&
    pizzaSizeRows.some((row) => getSizePriceItems(row).length > 0);
  // === END SAMPATH ===

  useEffect(() => {
    const productId = Number(props?.productId || 0);
    const shouldLoad =
      productId > 0 &&
      (isPizzaProduct || isDealProduct || isCreateYourOwnProduct);

    if (!shouldLoad) {
      setToppingPriceRows([]);
      return;
    }

    let cancelled = false;
    api
      .get(`/api/PizzaToppingPrice?productId=${productId}`)
      .then(({ data }) => {
        if (!cancelled) {
          setToppingPriceRows(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        if (!cancelled) setToppingPriceRows([]);
      });

    return () => {
      cancelled = true;
    };
  }, [
    props?.productId,
    isAdminInventoryCard,
    isPizzaProduct,
    isDealProduct,
    isCreateYourOwnProduct,
  ]);

  useEffect(() => {
    const productId = Number(props?.productId || 0);
    const shouldLoad = productId > 0;

    if (!shouldLoad) {
      setPizzaSizeRows([]);
      return;
    }

    let cancelled = false;
    api
      .get(`/api/PizzaSize/ByProductId?productId=${productId}`)
      .then(({ data }) => {
        if (!cancelled) {
          setPizzaSizeRows(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        if (!cancelled) setPizzaSizeRows([]);
      });

    return () => {
      cancelled = true;
    };
  }, [
    props?.productId,
  ]);

  // === ADDED BY SAMPATH – 2026-03-16 ===
  // Hide top/base price when resolved value is exactly 0.00
  const formatVisiblePrice = (value: number | null) => {
    if (value === null || value <= 0) return "";
    return `$${value.toFixed(2)}`;
  };

  // Prevent showing option/description text when it is just zero-like content
  const hasVisiblePriceDesc = (value: any) => {
    if (value === null || value === undefined) return false;
    const normalized = String(value).trim().replace(/\s+/g, "");
    return !["", "0", "0.0", "0.00", "$0", "$0.0", "$0.00"].includes(normalized);
  };
  const sizePriceDescText =
    getSizePriceDescText(pizzaSizeRows) ||
    getSizePriceDescText(displayToppingPriceRows);
  const visibleApiPriceDesc = hasVisiblePriceDesc(props.pricedesc)
    ? String(props.pricedesc).trim()
    : "";
  const productPriceDescText = sizePriceDescText || visibleApiPriceDesc;
  // === END SAMPATH ===

  const toTitleCase = (text: string) => {
    if (!text) return "";
    return text.toLowerCase().replace(/\b([a-z])/g, (m) => m.toUpperCase());
  };

  const getDisplayPriceText = () => {
    // === ADDED BY SAMPATH – 2026-03-05 ===
    // Safely resolve prices to avoid undefined access / invalid toFixed calls
    const safePrice = toSafeNumber(props?.price);
    const safeDeliveryPrice = toSafeNumber(props?.deliveryprice);
    const safeTakeoutPrice = toSafeNumber(props?.takeoutprice);
    // === END SAMPATH ===

    if (settings.ApplyTakeOutDeliveryPrice !== 0) {
      if (deliveryType !== "pickup") {
        // === ADDED BY SAMPATH – 2026-03-05 ===
        const resolvedDeliveryPrice =
          safeDeliveryPrice === 0 || safeDeliveryPrice === null
            ? safePrice
            : safeDeliveryPrice;
        // === ADDED BY SAMPATH – 2026-03-16 ===
        return formatVisiblePrice(resolvedDeliveryPrice);
        // === END SAMPATH ===
        // === END SAMPATH ===
      }

      // === ADDED BY SAMPATH – 2026-03-05 ===
      const resolvedTakeoutPrice =
        safeTakeoutPrice === 0 || safeTakeoutPrice === null
          ? safePrice
          : safeTakeoutPrice;
      // === ADDED BY SAMPATH – 2026-03-16 ===
      return formatVisiblePrice(resolvedTakeoutPrice);
      // === END SAMPATH ===
      // === END SAMPATH ===
    }

    // === ADDED BY SAMPATH – 2026-03-05 ===
    // === ADDED BY SAMPATH – 2026-03-16 ===
    return formatVisiblePrice(safePrice);
    // === END SAMPATH ===
    // === END SAMPATH ===
  };

  const ENABLE_SPL_DISCOUNT_BADGE = false;
  const specialDiscountPercent = Number(settings?.OnlineSpecialDiscount || 0);
  const activeOrderType = deliveryType === "pickup" ? "TakeOut" : "Delivery";
  const showSpecialDiscountBadge =
    ENABLE_SPL_DISCOUNT_BADGE &&
    specialDiscountPercent > 0 &&
    !window.location.pathname.includes("admin") &&
    ((activeOrderType === "Delivery" &&
      !!Number(settings?.AllowDiscountOnDelivery)) ||
      (activeOrderType === "TakeOut" &&
        !!Number(settings?.AllowDiscountOnPickup)));

  const handleCardClick = () => {
    if (typeof props.onClick === "function") props.onClick();
  };

  const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCardClick();
    }
  };

  const copyProductName = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const name = String(props?.productName || "").trim();
    if (!name) return;

    try {
      await navigator.clipboard.writeText(name);
    } catch (_error) {
      const textarea = document.createElement("textarea");
      textarea.value = name;
      textarea.setAttribute("readonly", "true");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
  };

  // === ADDED BY SAMPATH – 2026-01-07 ===
  // Force the + button to be a PERFECT 360° circle (equal width/height + hard rounded)

  //button color set theam + button
  // const plusBtnClassName =
  // "inline-flex items-center justify-center !rounded-full w-10 h-10 p-0 " +
  //  "border border-transparent bg-[#e4e5e5] shadow-sm hover:shadow-md hover:bg-[#d9dada] " +
  // "disabled:border-gray-200 disabled:bg-gray-200 disabled:text-gray-500";

  const plusBtnClassName =
    "inline-flex items-center justify-center !rounded-full w-5 h-10 p-0 " +
    "border border-transparent bg-[#fff] shadow-md hover:shadow-sm hover:bg-[#edf0f0] " +
    "disabled:border-gray-200 disabled:bg-gray-200 disabled:text-gray-500";

  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-14 ===
  // NEW: Move (+) button to RIGHT-BOTTOM over the image area (no extra margin / like screenshot)
  const showPlusButtonOnImageCorner = true;

  // Use theme color + transparency (falls back safely if theme vars not present)
  const plusBtnCornerStyle = {
    backgroundColor: "rgba(var(--color-card, 255,255,255), 0.92)",
    border: "1px solid rgba(var(--color-primary, 0,0,0), 0.10)",

    // === ADDED BY SAMPATH – 2026-02-02 ===
    // Force PLUS icon/text color to BLACK
    color: "#000000",
    // === END SAMPATH ===
  } as React.CSSProperties;

  const plusBtnCornerClassName =
    "inline-flex items-center justify-center !rounded-full w-10 h-10 p-0 " +
    "shadow-md hover:shadow-sm active:scale-[0.98] " +
    "disabled:opacity-60";
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-12 ===
  // Put "LARGE: $3.00" (pricedesc) at the BOTTOM, and move Price/+ button to TOP (under description)
  const showPriceDescUnderDescription = true;
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-12 ===
  // Remove aria-label block background/border, and force font color to #232121d9
  const priceDescStyle = {
    backgroundColor: "transparent",
    border: "none",
    color: "#232121ff",
    // === ADDED BY SAMPATH – 2026-03-16 ===
    fontWeight: 600,
    // === END SAMPATH ===
  } as React.CSSProperties;
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-12 ===
  // Remove ANY padding and align pricedesc to the LEFT (same start as Price)
  const priceDescContainerStyle = {
    padding: 0,
    margin: 0,
    backgroundColor: "transparent",
    border: "none",
  } as React.CSSProperties;
  // === END SAMPATH ===

  return (
    <div
      //Border Square / Grid Border Current 5
      className="relative z-0 bg-white shadow-sm hover:shadow-md transition-shadow w-full overflow-hidden border border-gray-500 rounded-[4px] flex flex-row items-stretch h-full min-h-[140px] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      aria-label={`Open ${props.productName}`}
    >
      {showSpecialDiscountBadge && (
        <div className="absolute right-[118px] md:right-[138px] top-3 z-10 inline-flex items-center gap-1 rounded-[2px] bg-[#f10505] px-2 py-1 text-white shadow-[0_5px_14px_rgba(220,38,38,0.22)]">
          <span className="text-[11px] font-bold leading-none tracking-normal">
            SPL
          </span>
          <span className="text-[12px] font-bold leading-none tracking-normal">
            {specialDiscountPercent}% off
          </span>
          <span className="inline-flex h-[11px] w-[11px] items-center justify-center rounded-full border border-white text-[8px] font-bold leading-none">
            i
          </span>
        </div>
      )}

      {/* === ADDED BY SAMPATH – 2026-01-12 === */}
      {/* Remove right padding so image can attach to the card edge (no separate box look) */}
      <div className="flex flex-1 flex-row pl-4 pr-0 py-1 h-full">
        {/* === END SAMPATH === */}
        <div className="flex-1 flex flex-col h-full">
          <div className="flex flex-col min-h-0">
            <div
              // === ADDED BY SAMPATH – 2026-01-12 ===
              // FIX: ensure row uses full height so image column can stretch with content (prevents bottom gap)
              className={`flex w-full gap-3 min-h-[72px] justify-between items-stretch ${
                hasAnyImage ? "" : "items-stretch"
              }`}
              // === END SAMPATH ===
            >
              <div
                className={`min-w-0 flex-1 ${hasAnyImage ? "" : "relative"} ${
                  showSpecialDiscountBadge ? "pr-20 md:pr-24" : ""
                }`}
              >
                <div className="flex flex-col h-full min-h-[72px]">
                  <Tooltip title={props.productName}>
                    <div className="mb-1 flex min-w-0 items-start gap-1.5">
                      <h3 className="min-w-0 font-semibold text-base md:text-lg text-itemColor line-clamp-2">
                        {props.productName}
                      </h3>
                      {isAdminInventoryCard && (
                        <>
                          <span className="mt-0.5 inline-flex h-6 shrink-0 items-center rounded-md border border-slate-200 bg-slate-50 px-1.5 text-[11px] font-semibold leading-none text-slate-600">
                            ID {props.productId}
                          </span>
                          <Tooltip title="Copy product name">
                            <button
                              type="button"
                              onClick={copyProductName}
                              className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 transition hover:border-primary/50 hover:bg-slate-50 hover:text-slate-900"
                              aria-label={`Copy ${props.productName}`}
                            >
                              <ContentCopyRoundedIcon className="!text-[14px]" />
                            </button>
                          </Tooltip>
                        </>
                      )}
                    </div>
                  </Tooltip>

                  {/* === ADDED BY SAMPATH – 2026-03-05 === */}
                  {/* Admin-only red issue flag for product setup problems (image missing is ignored) */}
                  {showAdminIssueFlag && (
                    <div className="mb-2">
                      <Tooltip title={adminIssueText || "Product configuration issue"}>
                        <span
                          className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold leading-none"
                          style={{
                            backgroundColor: "rgba(var(--color-danger, 220,38,38), 0.12)",
                            border: "1px solid rgba(var(--color-danger, 220,38,38), 0.28)",
                            color: "rgb(var(--color-danger, 220,38,38))",
                          }}
                        >
                          ⚑ Issue
                        </span>
                      </Tooltip>
                    </div>
                  )}
                  {/* === END SAMPATH === */}

                  <Tooltip title={props.productDescription}>
                    <p
                      className={`text-xs md:text-sm text-gray-600 break-words whitespace-pre-line ${
                        // === ADDED BY SAMPATH – 2026-01-14 ===
                        // Always clamp like "image mode" because we always show default image too
                        hasAnyImage ? "line-clamp-2" : "line-clamp-3"
                        // === END SAMPATH ===
                      }`}
                    >
                      {toTitleCase(props.productDescription || "")}
                    </p>
                  </Tooltip>

                  <div
                    className={`pt-2 w-full flex items-center gap-3 ${
                      // === ADDED BY SAMPATH – 2026-01-14 ===
                      // No need for extra right padding because + button is no longer absolute when no image
                      hasAnyImage ? "" : "pr-14"
                      // === END SAMPATH ===
                    }`}
                  >
                    {!!getDisplayPriceText() && (
                      <span className="text-sm md:text-base font-semibold text-gray-900">
                        {getDisplayPriceText()}
                      </span>
                    )}

                    {/* === ADDED BY SAMPATH – 2026-01-14 === */}
                    {/* Keep old (+) position available, but disable when using image-corner button */}
                    {!showPlusButtonOnImageCorner && (
                      <Button
                        className={`${plusBtnClassName} ml-auto`}
                        style={{ color: "#000" }}
                        aria-label={`Add ${props.productName} to cart`}
                        onClick={(e: any) => {
                          e?.stopPropagation?.();
                          if (typeof props.onClick === "function") props.onClick();
                        }}
                      >
                        <span className="text-[20px] leading-none font-semibold text-black text-gray-900">
                          +
                        </span>
                      </Button>
                    )}
                    {/* === END SAMPATH === */}
                  </div>

                  {showPriceDescUnderDescription &&
                    !!productPriceDescText && (
                      <div
                        className={showAdminProductMeta ? "mt-2" : "mt-auto"}
                        style={priceDescContainerStyle}
                      >
                        <Tooltip title={productPriceDescText || getDisplayPriceText()}>
                          <p
                            className="text-xs md:text-sm break-words line-clamp-2 normal-case whitespace-pre-line"
                            style={{
                              color: priceDescStyle.color,
                              margin: 0,
                              padding: 0,
                              // === ADDED BY SAMPATH – 2026-03-16 ===
                              fontWeight: priceDescStyle.fontWeight,
                              // === END SAMPATH ===
                            }}
                          >
                            {productPriceDescText}
                          </p>
                        </Tooltip>
                      </div>
                    )}

                  {showAdminProductMeta && (
                    <div className="mt-auto pt-1.5">
                      <div className="flex flex-wrap gap-1.5 rounded-[8px] border border-slate-200 bg-white/80 px-1.5 py-1.5">
                        {adminProductMetaItems.map((item) => (
                          <div
                            key={item.label}
                            className="inline-flex h-[24px] items-center gap-1 rounded-[6px] border border-slate-200 bg-slate-50 px-2 text-slate-800"
                          >
                            <span className="text-[10px] font-medium leading-none text-slate-500">
                              {item.label}
                            </span>
                            <span className="text-[12px] font-semibold leading-none text-slate-950">
                              {item.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {showAdminToppingPrices && (
                    <div className="mt-1 flex flex-wrap items-center gap-1 rounded-[8px] border border-emerald-100 bg-emerald-50/50 px-1.5 py-1">
                      <span className="px-1 text-[10px] font-medium leading-none text-emerald-800">
                        Topping prices
                      </span>
                      {displayToppingPriceRows.map((row: any, rowIndex: number) => (
                        getToppingPriceItems(row).length > 0 && (
                        <span
                          key={`${row?.sizeId || rowIndex}-${row?.sizeNameAbbr || "size"}`}
                          className="inline-flex min-h-5 flex-wrap items-center gap-1 rounded-[5px] border border-emerald-200 bg-white px-1.5 py-0.5 text-[10px] leading-none text-slate-700"
                        >
                          <span className="font-semibold text-emerald-800">
                            {row?.sizeNameAbbr || row?.sizeName || "Size"}
                          </span>
                          <span className="text-slate-300">|</span>
                          {getToppingPriceItems(row).map((item) => (
                            <span key={item.count} className="font-medium">
                              {item.count}:${item.price.toFixed(2)}
                            </span>
                          ))}
                        </span>
                        )
                      ))}
                    </div>
                  )}

                  {!showAdminToppingPrices && showAdminSizePrices && (
                    <div className="mt-1 flex flex-wrap items-center gap-1 rounded-[8px] border border-sky-100 bg-sky-50/50 px-1.5 py-1">
                      <span className="px-1 text-[10px] font-medium leading-none text-sky-800">
                        Size prices
                      </span>
                      {pizzaSizeRows.map((row: any, rowIndex: number) => (
                        getSizePriceItems(row).length > 0 && (
                          <span
                            key={`${row?.sizeId || rowIndex}-${row?.sizeName || "size"}`}
                            className="inline-flex min-h-5 flex-wrap items-center gap-1 rounded-[5px] border border-sky-200 bg-white px-1.5 py-0.5 text-[10px] leading-none text-slate-700"
                          >
                            <span className="font-semibold text-sky-800">
                              {String(row?.sizeName || "Size").slice(0, 2)}
                            </span>
                            <span className="text-slate-300">|</span>
                            {getSizePriceItems(row).map((item) => (
                              <span key={item.count} className="font-medium">
                                {item.count}:${item.price.toFixed(2)}
                              </span>
                            ))}
                          </span>
                        )
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* === ADDED BY SAMPATH – 2026-01-14 === */}
              {/* Always render image column; if no product image, show default noImage */}
              <div className="w-[110px] md:w-[130px] flex-shrink-0 relative overflow-hidden self-stretch h-[calc(100%+0.5rem)] -my-1">
                {/* === ADDED BY SAMPATH – 2026-01-14 === */}
                {/* NEW: (+) button on RIGHT-BOTTOM corner over the image (image behind button) */}
                {showPlusButtonOnImageCorner && (
                  <div className="absolute right-2 bottom-2 z-20">
                    <Button
                      className={plusBtnCornerClassName}
                      style={plusBtnCornerStyle}
                      aria-label={`Add ${props.productName} to cart`}
                      onClick={(e: any) => {
                        e?.stopPropagation?.();
                        if (typeof props.onClick === "function") props.onClick();
                      }}
                    >
                      <span className="text-[20px] leading-none font-semibold">
                        +
                      </span>
                    </Button>
                  </div>
                )}
                {/* === END SAMPATH === */}

                <div className="relative h-full min-h-[140px]">
                  {loading && (
                    <div className="absolute inset-0 bg-gray-50 flex items-center justify-center">
                      <div className="w-10 h-10 border-4 border-gray-300 border-t-primary rounded-full animate-spin"></div>
                    </div>
                  )}

                  <img
                    src={resolvedImageUrl}
                    className={`${
                      loading ? "opacity-0" : "opacity-100"
                    } absolute inset-0 w-full h-full object-cover transition-opacity`}
                    onLoad={() => setLoading(false)}
                    onError={(e) => {
                      e.currentTarget.src = noImage;
                      setLoading(false);
                    }}
                    alt=""
                  />
                </div>
              </div>
              {/* === END SAMPATH === */}
            </div>

            {!showPriceDescUnderDescription &&
              !!productPriceDescText && (
                <div className="mt-2" style={priceDescContainerStyle}>
                  <Tooltip title={productPriceDescText || getDisplayPriceText()}>
                    <p
                      className="text-xs md:text-sm break-words line-clamp-2 normal-case whitespace-pre-line"
                      style={{
                        color: priceDescStyle.color,
                        margin: 0,
                        padding: 0,
                        // === ADDED BY SAMPATH – 2026-03-16 ===
                        fontWeight: priceDescStyle.fontWeight,
                        // === END SAMPATH ===
                      }}
                    >
                      {productPriceDescText}
                    </p>
                  </Tooltip>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Product;
